import {MapPinIcon, Square3Stack3DIcon, TruckIcon} from "@heroicons/react/20/solid";
import {type ReactNode, useId} from "react";
import {OtsIconActionButton} from "../components/OtsButton";
import {OtsPopover} from "../components/OtsPopover";
import {OtsSegmentedControl} from "../components/OtsSegmentedControl";
import {OtsSwitch} from "../components/OtsSwitch";
import {OtsThemeRoot} from "../theme/OtsTheme";
import {
  DEFAULT_LIVE_MAP_DISPLAY,
  formatStaleMinutes,
  LIVE_MAP_STALE_MINUTES,
  type LiveMapBase,
  type LiveMapDisplayOptions,
  type LiveMapGeofences,
  LIVE_MAP_SITE_LINK_RADII,
  type LiveMapSiteLinks,
} from "./live-map-display";
import {SITE_ROAD_ROUTE_LIMIT} from "./site-connections";

export type LiveMapEntity = "PERSON" | "TRAILER" | "VEHICLE";

export type LiveMapControlsProps = {
  entity: LiveMapEntity;
  onEntityChange: (entity: LiveMapEntity) => void;
  display: LiveMapDisplayOptions;
  onDisplayChange: (display: LiveMapDisplayOptions) => void;
  /** Ops Mode swaps the site markers for operational status; only HubGo offers it. */
  showOpsMode?: boolean;
  opsMode?: boolean;
  onOpsModeChange?: (opsMode: boolean) => void;
  /** Zooms to the tracked units; the button shows only when given. */
  onFitFleet?: () => void;
  /** Zooms to the region's sites; the button shows only when given. */
  onFitSites?: () => void;
  /** The app's defaults; the options button lights up while the display differs from them. */
  defaults?: LiveMapDisplayOptions;
  className?: string;
};

const ENTITY_SEGMENTS: {id: LiveMapEntity; label: string}[] = [
  {id: "PERSON", label: "Drivers"},
  {id: "TRAILER", label: "Trailers"},
  {id: "VEHICLE", label: "Vehicles"},
];

const BASE_SEGMENTS: {id: LiveMapBase; label: string}[] = [
  {id: "satellite", label: "Satellite"},
  {id: "roadmap", label: "Map"},
];

const GEOFENCE_SEGMENTS: {id: LiveMapGeofences; label: string}[] = [
  {id: "zoomed", label: "Zoomed in"},
  {id: "always", label: "Always"},
  {id: "off", label: "Off"},
];

const SITE_LINK_SEGMENTS: {id: LiveMapSiteLinks; label: string}[] = [
  {id: "nearest", label: "Nearest"},
  {id: "headed", label: "Headed here"},
  {id: "off", label: "Off"},
];

const RADIUS_SEGMENTS = LIVE_MAP_SITE_LINK_RADII.map(miles => ({id: String(miles), label: `${miles} mi`}));

const STALE_SEGMENTS = LIVE_MAP_STALE_MINUTES.map(minutes => ({id: String(minutes), label: formatStaleMinutes(minutes)}));

const heading = "text-[11px] font-semibold uppercase tracking-wide text-[var(--ots-text)]";
const subheading = "mt-1 text-xs font-semibold text-[var(--ots-secondary)]";

/**
 * A titled group of options. Not a fieldset: Mantine's global reset recolors a legend in DispatchGo and HubGo,
 * and its unlayered CSS beats Tailwind's classes, so a legend and a span heading drifted apart there.
 */
const Section = ({title, children}: {title: string; children: ReactNode}) => {
  const id = useId();
  return <div role="group" aria-labelledby={id} className="flex flex-col gap-1.5">
    <span id={id} className={heading}>{title}</span>
    {children}
  </div>;
};

/** Whether anything differs from the defaults the map opens with, so the button can say so. */
const isCustomized = (display: LiveMapDisplayOptions, defaults: LiveMapDisplayOptions) =>
  (Object.keys(defaults) as (keyof LiveMapDisplayOptions)[]).some(key => display[key] !== defaults[key]);

/** The live map's floating bar: which fleet to track, Ops Mode, where to look, and what to draw. */
export const LiveMapControls = ({
  entity,
  onEntityChange,
  display,
  onDisplayChange,
  showOpsMode = false,
  opsMode = false,
  onOpsModeChange,
  onFitFleet,
  onFitSites,
  defaults = DEFAULT_LIVE_MAP_DISPLAY,
  className,
}: LiveMapControlsProps) => {
  const satellite = display.baseMap === "satellite";
  const set = (fields: Partial<LiveMapDisplayOptions>) => onDisplayChange({...display, ...fields});
  return <OtsThemeRoot
    className={[
      "!flex items-center gap-2 rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] p-1 text-[var(--ots-text)] shadow-md",
      className,
    ].filter(Boolean).join(" ")}>
    <OtsSegmentedControl<LiveMapEntity>
      size="compact"
      ariaLabel="Tracked fleet"
      value={entity}
      onChange={onEntityChange}
      segments={ENTITY_SEGMENTS}/>
    {showOpsMode && (
      <OtsSwitch
        size="compact"
        className="items-center px-1"
        label="Ops Mode"
        checked={opsMode}
        onCheckedChange={onOpsModeChange}/>
    )}
    {(onFitFleet || onFitSites) && <span aria-hidden="true" className="h-5 w-px bg-[var(--ots-outline-variant)]"/>}
    {onFitFleet && (
      <OtsIconActionButton variant="text" size="compact" className="!overflow-hidden" label="Fit to fleet" icon={<TruckIcon/>} onClick={onFitFleet}/>
    )}
    {onFitSites && (
      <OtsIconActionButton variant="text" size="compact" className="!overflow-hidden" label="Fit to sites" icon={<MapPinIcon/>} onClick={onFitSites}/>
    )}
    <OtsPopover
      density="compact"
      // Opens rightward from the button; the anchor shifts it back in when the window is too narrow.
      placement="bottom start"
      ariaLabel="Display options"
      tooltip="Display options"
      pressed={isCustomized(display, defaults)}
      buttonClassName="inline-flex size-8 items-center justify-center !overflow-hidden rounded-md text-[var(--ots-primary)] outline-none hover:bg-[var(--ots-primary-container)] focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)] aria-pressed:bg-[var(--ots-primary-container)]"
      panelClassName="grid w-[38rem] !max-w-[calc(100vw-2rem)] grid-cols-1 gap-x-6 gap-y-3 !p-3 sm:grid-cols-2"
      trigger={<Square3Stack3DIcon aria-hidden="true" className="size-[18px]"/>}>
      {/* What the map draws on the left; what a clicked site shows on the right. One column when narrow. */}
      <div data-display-column="map" className="flex flex-col gap-3">
        <Section title="Base map">
          <OtsSegmentedControl<LiveMapBase>
            size="compact"
            ariaLabel="Base map"
            value={display.baseMap}
            onChange={(baseMap) => set({baseMap})}
            segments={BASE_SEGMENTS}/>
        </Section>
        <Section title="Overlays">
          <OtsSwitch
            size="compact"
            label="Streets"
            description={satellite ? "Road lines and street names over the imagery." : "The map view always shows streets."}
            disabled={!satellite}
            checked={satellite ? display.streetOverlay : true}
            onCheckedChange={(streetOverlay) => set({streetOverlay})}/>
          <OtsSwitch
            size="compact"
            label="Traffic"
            description="Live traffic speeds on the roads."
            checked={display.traffic}
            onCheckedChange={(traffic) => set({traffic})}/>
        </Section>
        <Section title="Units">
          <OtsSwitch
            size="compact"
            label="Load rings"
            description="How full each unit is, in its cargo's color; red over capacity."
            checked={display.unitLoads}
            onCheckedChange={(unitLoads) => set({unitLoads})}/>
          <OtsSwitch
            size="compact"
            label="Name labels"
            description="Names under the markers once zoomed in."
            checked={display.labels}
            onCheckedChange={(labels) => set({labels})}/>
          <OtsSwitch
            size="compact"
            label="Dim stale positions"
            description={`Fades units with no ping in ${formatStaleMinutes(display.staleAfterMinutes)}.`}
            checked={display.dimStale}
            onCheckedChange={(dimStale) => set({dimStale})}/>
          {display.dimStale && (
            <OtsSegmentedControl<string>
              size="compact"
              className="ml-9 self-start"
              ariaLabel="Stale after"
              value={String(display.staleAfterMinutes)}
              onChange={(minutes) => set({staleAfterMinutes: Number(minutes)})}
              segments={STALE_SEGMENTS}/>
          )}
        </Section>
      </div>
      <div data-display-column="sites" className="flex flex-col gap-3">
        <Section title="Sites">
          <OtsSwitch
            size="compact"
            label="Avail. Loads"
            description="A count on each site with loads to haul; red when a container there is critical."
            checked={display.siteLoads}
            onCheckedChange={(siteLoads) => set({siteLoads})}/>
          <span className={subheading}>Geofences</span>
          <OtsSegmentedControl<LiveMapGeofences>
            size="compact"
            ariaLabel="Site geofences"
            value={display.geofences}
            onChange={(geofences) => set({geofences})}
            segments={GEOFENCE_SEGMENTS}/>
          <span className={subheading}>Lines to units</span>
          <OtsSegmentedControl<LiveMapSiteLinks>
            size="compact"
            ariaLabel="Lines to units"
            value={display.siteLinks}
            onChange={(siteLinks) => set({siteLinks})}
            segments={SITE_LINK_SEGMENTS}/>
          <span className="text-[11px] text-[var(--ots-text-muted)]">
            {display.siteLinks === "headed"
              ? "Units that may be bound for the site you click."
              : display.siteLinks === "nearest" ? "The closest units to the site you click." : "No lines when you click a site."}
          </span>
          {display.siteLinks === "nearest" && (
            <OtsSegmentedControl<string>
              size="compact"
              className="self-start"
              ariaLabel="Within"
              value={String(display.siteLinkRadiusMiles)}
              onChange={(miles) => set({siteLinkRadiusMiles: Number(miles)})}
              segments={RADIUS_SEGMENTS}/>
          )}
          {display.siteLinks !== "off" && (
            <OtsSwitch
              size="compact"
              label="Follow roads"
              description={`Drive time and road route for the closest ${SITE_ROAD_ROUTE_LIMIT}.`}
              checked={display.siteRoadRoutes}
              onCheckedChange={(siteRoadRoutes) => set({siteRoadRoutes})}/>
          )}
        </Section>
      </div>
    </OtsPopover>
  </OtsThemeRoot>;
};
